import { z } from 'zod';

/**
 * Typed env loader for apps/web. Vite exposes vars prefixed with VITE_ via
 * `import.meta.env`. We validate them at module load so a missing required
 * var fails immediately, not deep inside a hook.
 */
const EnvSchema = z.object({
  VITE_GRAPHQL_ENDPOINT: z.string().min(1, 'VITE_GRAPHQL_ENDPOINT is required'),
  VITE_AUTH0_DOMAIN: z.string().min(1, 'VITE_AUTH0_DOMAIN is required'),
  VITE_AUTH0_CLIENT_ID: z.string().min(1, 'VITE_AUTH0_CLIENT_ID is required'),
  VITE_AUTH0_AUDIENCE: z.string().min(1, 'VITE_AUTH0_AUDIENCE is required'),
  VITE_AUTH0_REDIRECT_URI: z.string().url('VITE_AUTH0_REDIRECT_URI must be a URL'),
  // Optional. A blank value in `.env` arrives as `''` (not `undefined`), and
  // `''` fails `.url()` even with `.optional()` — which would throw at module
  // load and WHITE-SCREEN the whole app over a non-critical (sign-out target)
  // var. Coerce empty/whitespace to `undefined` so a blank var is truly absent.
  VITE_MARKETING_URL: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().url().optional(),
  ),
  // Optional MCP host override (read by the MCP playground page). Same
  // empty-string→undefined coercion so a blank value doesn't fail validation
  // and white-screen the app.
  VITE_MCP_HOST_URL: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
  // Optional public API origin (ADR-0796) — the domain the WhatsApp webhook URL
  // is composed on. Absent locally (derives from the GraphQL endpoint); the
  // deployed build bakes it from the registry mirror, because the endpoint the
  // dashboard itself calls is the bare Function URL, which the WAF does not front.
  VITE_API_PUBLIC_URL: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().url().optional(),
  ),
  // Optional PostHog project API key (public, write-only `phc_…`). When absent
  // — the local-dev default — analytics is a no-op, so `pnpm dev` never ships
  // events. Injected by CDK/Amplify in the deployed environments. Same
  // empty-string→undefined coercion so a blank var is treated as truly absent.
  VITE_POSTHOG_KEY: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
  // Optional PostHog ingestion host. Defaults to the EU cloud (data-residency
  // aligned with eu-central-1) when unset; override to `https://us.i.posthog.com`
  // for a US-cloud project. See docs/decisions/0077-product-analytics-posthog.md.
  VITE_POSTHOG_HOST: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().url().optional(),
  ),
  // Optional publishable key for embedding Syncanix's OWN chat widget on the
  // dashboard (dogfood). Public (`pk_…`, ships in page source). Absent → no
  // widget (the common case for a customer dashboard build). Same
  // empty-string→undefined coercion so a blank var is truly absent.
  VITE_SYNCANIX_WIDGET_KEY: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
  // Optional API-origin override for the dogfood widget (defaults derive from
  // the key prefix — see `resolveWidgetEmbed`).
  VITE_SYNCANIX_WIDGET_ORIGIN: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
  // Where the dogfood widget EXECUTES a capability — our own API. A different
  // axis from `_ORIGIN` above, which is the Syncanix control plane (config +
  // turn stream). They diverge here by construction: the dashboard is served
  // from `app.` and its API lives on `api.`, so absent this the transport would
  // default to same-origin and aim every capability call at the SPA host.
  VITE_SYNCANIX_WIDGET_API_BASE_URL: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
  // Optional Paddle.js client-side token (`test_…` sandbox / `live_…` production;
  // public by design — ADR-0092/ADR-0118). Read by the `/checkout` page to
  // initialise Paddle.js and open the hosted-checkout overlay for the `?_ptxn`
  // transaction. Absent → the checkout page shows a "not configured" state. Set
  // it in the deploy env (baked by web.sh from the SSM config mirror) or in
  // apps/web/.env for local sandbox testing. The prefix selects the Paddle.js
  // environment (`test_` → sandbox, `live_` → production).
  VITE_PADDLE_CLIENT_TOKEN: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().optional(),
  ),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  const issues = parsed.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
  // Fail loudly with a readable message in dev console + browser.
  throw new Error(`Invalid web environment configuration:\n${issues}`);
}

export const env = parsed.data;
export type Env = typeof env;
